import axios from 'axios';
import { Moment } from 'moment';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { present } from '@/shared/utils/typescript';
import { formatDiagnostic, formatDiagnosticContent, parseDiagnostic, parseDiagnostics, parseDiagnosticType } from '../converters/diagnosticConverters';
import { parseDiagnosticTemplates } from '../converters/diagnosticTemplateConverters';
import { JobId } from '../models/AbstractJob';
import Diagnostic, { DiagnosticId } from '../models/diagnosis/Diagnostic';
import { DiagnosticData } from '../models/diagnosis/DiagnosticData';
import DiagnosticTemplate, { DiagnosticTemplateId } from '../models/diagnosis/DiagnosticTemplate';
import DiagnosticTypeDescription from '../models/diagnosis/DiagnosticTypeDescription';
import MediaFile2 from '@/general/models/MediaFile2';

class DiagnosticClient {
  async loadDiagnostics(jobId: JobId, dossierId: DossierId): Promise<Array<Diagnostic>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics`);
    return parseDiagnostics(response.data);
  }

  async loadDiagnosticTypes(dossierId: DossierId): Promise<Array<DiagnosticTypeDescription>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/diagnosticsteps`);
    return parseDiagnosticType(response.data);
  }

  async createDiagnostic(diagnostic: Diagnostic, data: DiagnosticData, dossierId: DossierId, jobId: JobId): Promise<Diagnostic> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics`, formatDiagnostic(diagnostic, data));
    return present(parseDiagnostic(response.data));
  }

  async updateDiagnostic(diagnostic: Diagnostic, data: DiagnosticData, dossierId: DossierId, jobId: JobId): Promise<Diagnostic> {
    const response = await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics/${diagnostic.id}`, formatDiagnosticContent(data, diagnostic.type));
    return present(parseDiagnostic(response.data));
  }

  async start(timerStartedAt: Moment, diagnosticId: DiagnosticId, jobId: JobId, dossierId: DossierId): Promise<Diagnostic> {
    const response = await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics/${diagnosticId}`, { timerStarted: timerStartedAt });
    return present(parseDiagnostic(response.data));
  }

  async stop(timerStoppedAt: Moment, diagnosticId: DiagnosticId, jobId: JobId, dossierId: DossierId): Promise<Diagnostic> {
    const response = await axios.patch(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics/${diagnosticId}`, { timerStopped: timerStoppedAt });
    return present(parseDiagnostic(response.data));
  }

  async remove(diagnosticId: DiagnosticId, jobId: JobId, dossierId: DossierId): Promise<void> {
    await axios.delete(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics/${diagnosticId}`);
  }

  async loadTemplates(dossierId: DossierId): Promise<Array<DiagnosticTemplate>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/diagnostictemplates`);
    return parseDiagnosticTemplates(response.data);
  }

  async addTemplate(templateId: DiagnosticTemplateId, jobId: JobId, dossierId: DossierId): Promise<Array<Diagnostic>> {
    const response = await axios.post(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics/template/${templateId}`);
    return parseDiagnostics(response.data);
  }

  async linkMedia(files: Array<MediaFile2>, isLinked: boolean, dossierId: DossierId, jobId: JobId, diagnosticId: DiagnosticId): Promise<void> {
    const promises = files.map(mf => {
      return axios.put(`/api/mobopro/v1/dossiers/${dossierId}/jobs/${jobId}/diagnostics/${diagnosticId}/media`, {
        mediaFileId: mf.id,
        isLinked,
      });
    });
    await Promise.all(promises);
  }
}

export default new DiagnosticClient();